// ------------------------------------------------------------
// useBaseForm.js (Vue 3 + Pinia)
//
// Base composable for all method/shared child forms: field mapping, form state, parent sync,
// reference/planned mirrors and Pinia integration.
//
// The parent owns `modelValue`; the child declares a `fields` map (frontendKey -> backendKey).
// The composable keeps the reactive `form` for template bindings and exposes a `data` computed
// that translates between the frontend and backend shapes.
//
// Data flow: parent modelValue -> watcher 1 ingests into `form`; child edit -> watcher 2 emits
// update:modelValue. An `ingestingFromParent` flag keeps the two from looping.
// ------------------------------------------------------------

import { reactive, ref, computed, watch, nextTick } from 'vue'
import { useProjectStore } from '@/stores/projectStore'
import { storeToRefs } from 'pinia'
import api from '@/plugins/axios'

/**
 * useBaseForm
 *
 * @param {object}                          options
 * @param {import('vue').Ref<object>}       options.value         - parent's v-model ref (modelValue)
 * @param {object}                          options.fields        - REQUIRED: frontendKey -> backendKey map
 * @param {object}                          [options.defaults]    - optional per-field default values (frontend keys)
 * @param {import('vue').Ref<boolean>}      options.reference     - whether this form is in reference mode
 * @param {import('vue').Ref<boolean>}      options.planned       - whether this form is in planned mode
 * @param {import('vue').Ref<boolean>}      options.shared        - whether this is the shared sub-form
 * @param {import('vue').Ref<object>}       options.otherData     - the sibling data (reference or planned, whichever is not active)
 * @param {import('vue').Ref<object>}       options.sharedData    - shared form's current data (merged into normal forms)
 * @param {import('vue').Ref<string>}       options.methodValue   - current method key (used for hint fetching)
 * @param {Function}                        options.emit          - component emit function
 */
export function useBaseForm({
  value,
  fields,
  defaults = {},
  reference,
  planned,
  shared,
  otherData,
  sharedData,
  methodValue,
  emit,
}) {

  // ============================================================
  // SECTION 1 — Validation & Bookkeeping
  // ============================================================

  // Guard: every child must supply a non-empty fields map.
  // Without it, the composable cannot translate between frontend and backend shapes.
  if (!fields || typeof fields !== 'object' || !Object.keys(fields).length) {
    throw new Error('[useBaseForm] `fields` map is required and must not be empty.')
  }

  // `form` is the reactive container for all frontend field values.
  // Child templates bind directly to form.fieldName (e.g. v-model="form.powerOutput").
  // Properties are populated by data.set() when the parent value changes.
  const form = reactive({})

  // `preservedMeta` holds backend-only metadata fields that arrive with the payload
  // but are NOT part of the editable form (id, polymorphic_ctype, status, etc.).
  // These must survive the round-trip so the backend can identify and classify the object,
  // but they should never be shown as form fields or lost on save.
  const preservedMeta = ref({})

  // The list of backend keys that are treated as metadata, not form data.
  // Any key in the incoming payload that matches this list is preserved separately.
  const PRESERVED_META_KEYS = [
    'id',
    'method_key',
    'polymorphic_ctype',
    'status',
    'deleted',
    'created_at',
    'updated_at',
    'name',
  ]

  // `exposeFields` is the list of backend field names this child owns.
  // Used by shared forms' parent to filter validation errors to the right child.
  const exposeFields = computed(() => Object.values(fields))


  // ============================================================
  // SECTION 2 — Pinia Store Integration (Generator Types)
  // ============================================================

  // We pull the project's action list from the store to derive generator type options.
  // This is only relevant for child forms that have a `generatorType` field;
  // forms without it will simply get an empty `filteredGeneratorNames` list.
  const projectStore = useProjectStore()
  const { currentProjectActions: actionList } = storeToRefs(projectStore)

  // `generatorTypes` is the deduplicated list of available generator method types
  // (e.g. ['heatpump', 'boiler', 'freegen']), used to populate type dropdowns.
  const generatorTypes = ref([])

  function fetchGeneratorTypes() {
    const gens = (actionList.value || [])
      .filter(a => a.method_type === 'generator' && a.method !== 'chiller')
      .map(a => a.method)
    generatorTypes.value = [...new Set(gens)]
  }

  // `filteredGeneratorNames` returns the action list entries matching the currently
  // selected generator type. Handles the special 'freegen' case (filters by energy type).
  // Returns empty array if this child form does not have a `generatorType` field.
  const filteredGeneratorNames = computed(() => {
    // Only applicable if this child declared a `generatorType` frontend field.
    const hasGeneratorType = 'generatorType' in fields
    const generatorTypeValue = hasGeneratorType ? form['generatorType'] : null

    if (!generatorTypeValue) return []

    if (generatorTypeValue === 'freegen') {
      return (actionList.value || []).filter(
        a => a.method === 'freegen' && a?.planned?.main_generated_energy === 11
      )
    }

    return (actionList.value || []).filter(a => a.method === generatorTypeValue)
  })


  // ============================================================
  // SECTION 3 — Core: data computed (frontend <-> backend translation)
  // ============================================================

  // `data` translates between the frontend form shape and the backend payload, both ways.
  //
  // get() — backend-shaped object from the current form: own fields + preserved metadata for a
  //   shared form, plus sharedData for a normal one. Own fields win (rightmost spread).
  // set() — ingests a backend-shaped object: preserved metadata (id etc.) into preservedMeta,
  //   each backend key mapped back to its frontend key, missing keys filled from `defaults`.

  const data = computed({

    get() {
      const out = {}

      for (const [front, back] of Object.entries(fields)) {
        // Include all fields that have a real value.
        // false and 0 are valid values and are included.
        // Only skip null and undefined (fields that were never set or were cleared).
        if (form[front] !== null && form[front] !== undefined) {
          out[back] = form[front]
        }
      }

      // Always include the reference flag so the backend knows which data set this is.
      out.is_reference = !!reference.value

      if (shared.value) {
        // Shared form: only return own fields + preserved metadata.
        // Do NOT merge sharedData here — the shared form IS the shared data.
        return {
          ...preservedMeta.value,
          ...out,
        }
      }

      // Normal method form: merge shared fields first, then own fields on top.
      // preservedMeta sits between them so it can be overridden by own form fields
      // if there happens to be a naming collision (unlikely but safe).
      return {
        ...(sharedData.value || {}),   // guard: sharedData may be null before shared form loads
        ...preservedMeta.value,
        ...out,
      }
    },

    set(newVal) {
      if (!newVal || typeof newVal !== 'object') {
        // No incoming value — reset all fields to their defaults (or null).
        for (const front of Object.keys(fields)) {
          form[front] = Object.prototype.hasOwnProperty.call(defaults, front)
            ? defaults[front]
            : null
        }
        preservedMeta.value = {}
        return
      }

      // Extract backend-only metadata before mapping form fields.
      // These keys travel with the payload but are not editable form fields.
      const meta = {}
      for (const key of PRESERVED_META_KEYS) {
        if (Object.prototype.hasOwnProperty.call(newVal, key)) {
          meta[key] = newVal[key]
        }
      }
      preservedMeta.value = meta

      // Map each backend key back to its frontend form field.
      // If the incoming payload doesn't have a key, fall back to the declared default,
      // or null if no default was declared.
      for (const [front, back] of Object.entries(fields)) {
        const fallback = Object.prototype.hasOwnProperty.call(defaults, front)
          ? defaults[front]
          : null
        form[front] = Object.prototype.hasOwnProperty.call(newVal, back)
          ? newVal[back]
          : fallback
      }
    },
  })


  // ============================================================
  // SECTION 4 — Reference / Planned mirrors
  // ============================================================

  // `referenceData` returns the reference-mode data for this form pair.
  // When this form IS the reference form, it returns its own data.
  // When this form IS NOT the reference form, it returns the sibling's data.
  // Used by copyFromReference() and by templates that display reference values alongside inputs.
  const referenceData = computed(() =>
    reference.value ? data.value : otherData.value
  )

  // `plannedData` mirrors the same pattern for planned mode.
  const plannedData = computed(() =>
    planned.value ? data.value : otherData.value
  )


  // ============================================================
  // SECTION 5 — Parent <-> Internal sync (watchers)
  // ============================================================

  // `ingestingFromParent` is a dirty flag that breaks the potential emit loop.
  //
  // Without this flag:
  //   parent changes modelValue --> watcher 1 sets form --> watcher 2 emits --> parent
  //   updates modelValue --> watcher 1 fires again --> infinite loop.
  //
  // With the flag:
  //   watcher 1 sets the flag before ingesting, clears it on nextTick.
  //   watcher 2 skips the emit while the flag is set.
  //   Child-initiated changes (user typing in a field) never set the flag,
  //   so they always emit normally.
  const ingestingFromParent = ref(false)

  // Watcher 1: Parent -> Internal
  // Fires when the parent's modelValue changes (e.g. after a save/load cycle).
  // Ingests the new backend-shaped value into the form via data.set().
  watch(
    value,
    v => {
      const incoming = (v && typeof v === 'object') ? v : {}
      ingestingFromParent.value = true
      data.value = incoming
      // Defer clearing the flag until after Vue has propagated the form changes,
      // so watcher 2 doesn't fire during this ingestion cycle.
      nextTick(() => {
        ingestingFromParent.value = false

        // Ingestion is not value-preserving: data.set() fills missing keys from `defaults` while
        // watcher 2 is suppressed, so without this the default lives only in `form` — invisible
        // to the parent and to every sibling form.
        //
        // That matters for a SHARED form: its `calculation_method` gates which sections the
        // sibling forms render. Left unemitted, sharedData stays {}, the gated sections stay
        // hidden, and the backend rejects the measure for fields the user was never shown.
        //
        // No loop: the parent re-ingests an identical payload, `form` does not change, the
        // computed is not invalidated and watcher 2 does not re-fire.
        const filledFromDefaults = Object.values(fields).some(
          back => incoming[back] === undefined && data.value[back] !== undefined
        )
        if (filledFromDefaults) {
          emit('update:modelValue', data.value)
        }
      })
    },
    { immediate: true, deep: true }
  )

  // Watcher 2: Internal -> Parent
  // Fires when any form field changes (user input or programmatic update).
  // Emits the current backend-shaped payload to the parent via update:modelValue.
  // Skipped entirely when the change originated from the parent (ingestingFromParent flag).
  watch(
    data,
    newVal => {
      if (ingestingFromParent.value) return
      emit('update:modelValue', newVal)
    },
    { deep: true }
  )

  // Keep generator types up to date when the project's action list changes.
  watch(actionList, fetchGeneratorTypes, { immediate: true })


  // ============================================================
  // SECTION 6 — Hints & Helpers
  // ============================================================

  // `hint` holds the help text for each backend field key, fetched from the API.
  // Templates display these as tooltips or inline descriptions next to inputs.
  const hint = ref({})

  // Fetches help text for all fields of the current method from the backend.
  // Keyed by backend field name (e.g. hint.value['power_output']).
  async function fetchHints() {
    try {
      const res = await api.get(`/methods/${methodValue.value}/help/`)
      hint.value = Object.assign(
        {},
        ...res.data.map(x => ({ [x.key]: x.help }))
      )
    } catch {
      hint.value = {}
    }
  }

  /**
   * copyFromReference(backendKey)
   *
   * Generic "Referenzwert übernehmen" (copy from reference) helper.
   * Finds the frontend field that maps to the given backend key, then
   * sets it to the current reference value for that field.
   *
   * Used by child templates to implement "use reference value" buttons
   * without needing to know the internal field mapping themselves.
   *
   * @param {string} backendKey - the backend field name (e.g. 'power_output')
   */
  function copyFromReference(backendKey) {
    const entry = Object.entries(fields).find(([, back]) => back === backendKey)

    if (!entry) {
      // Warn loudly in development — a wrong key here is a silent no-op otherwise.
      console.warn(
        `[useBaseForm] copyFromReference: backend key "${backendKey}" not found in this form's fields map.`,
        'Available backend keys:',
        Object.values(fields)
      )
      return
    }

    const [front] = entry
    form[front] = referenceData.value?.[backendKey] ?? form[front]
  }

  // Backwards-compatible alias for components that previously called getReferenceParameter().
  function getReferenceParameter(parameter) {
    copyFromReference(parameter)
  }


  // ============================================================
  // PUBLIC API
  // ============================================================

  return {
    // --- Template bindings ---
    form,                    // reactive object; child templates bind to form.fieldName
    hint,                    // help text keyed by backend field name
    generatorTypes,          // deduplicated list of generator method types

    // --- Computed ---
    data,                    // backend-shaped payload (get) / ingestion handler (set)
    filteredGeneratorNames,  // action list entries matching current generatorType
    referenceData,           // reference-mode data (own or sibling depending on mode)
    plannedData,             // planned-mode data (own or sibling depending on mode)
    exposeFields,            // array of backend keys this child owns (for shared form validation)

    // --- Methods ---
    fetchHints,              // fetches help text for current method from API
    fetchGeneratorTypes,     // refreshes generator type list from store
    copyFromReference,       // sets a field to its current reference value by backend key
    getReferenceParameter,   // alias for copyFromReference (backwards compatibility)
  }
}